import {
  CalendarRangeIcon,
  CheckCircle,
  DollarSign,
  FolderIcon,
  HouseIcon,
  Menu,
  MessageSquare,
  Search,
  Settings,
  SquareUserRound,
  Star,
  ThumbsUp,
  UserCheck2,
  Wallet,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import SidebarItem from "@/components/Sidebar/SidebarItem";
import { SidebarCollapseToggle } from "@/components/Sidebar/sidebar-collapse-toggle";
import { useSidebarCollapsed } from "@/components/Sidebar/use-sidebar-collapsed";
import { TooltipProvider } from "@/components/ui/tooltip";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { useRouterState } from "@tanstack/react-router";
import { DownloadApp } from "../DownloadApp";

import BottomBarItem from "../BottomBar/BottomBarItem";
import { BottomBar } from "@/components/BottomBar/bottom-bar";
import { User } from "@/common/types/user";
import { useTRPC } from "@/trpc";

function ProviderChatSidebarItem({
  item,
  onClick,
  collapsed,
}: {
  item: {
    icon: React.ReactNode;
    label: string;
    route?: string;
  };
  onClick?: () => void;
  collapsed?: boolean;
}) {
  const trpc = useTRPC();
  const { data: unreadMessagesCount } = useQuery(
    trpc.chat.getUnreadMessagesCount.queryOptions({}),
  );

  const notificationCount =
    unreadMessagesCount?.reduce(
      (acc, message) => acc + message.unreadCount,
      0,
    ) ?? 0;

  return (
    <SidebarItem
      onClick={onClick}
      collapsed={collapsed}
      notificationCount={notificationCount}
      item={item}
      variant="primary"
    />
  );
}

function ProviderChatBottomMenuItem({
  item,
}: {
  item: {
    icon: React.ReactNode;
    label: string;
    route: string;
  };
}) {
  const trpc = useTRPC();
  const { data: unreadMessagesCount } = useQuery(
    trpc.chat.getUnreadMessagesCount.queryOptions({}),
  );

  const notificationCount =
    unreadMessagesCount?.reduce(
      (acc, message) => acc + message.unreadCount,
      0,
    ) ?? 0;

  return <BottomBarItem notificationCount={notificationCount} item={item} />;
}

const sideItems = [
  {
    icon: <HouseIcon />,
    label: "Home",
    route: "/provider/home",
  },
  {
    icon: <FolderIcon />,
    label: "Documents",
    route: "/provider/documents",
  },
  {
    icon: <SquareUserRound />,
    label: "Profile",
    route: "/provider/profile",
  },
  {
    icon: <CalendarRangeIcon />,
    label: "Jobs",
    route: "/provider/jobs",
    subItems: [
      {
        icon: <CheckCircle />,
        label: "My Jobs",
        route: "/provider/jobs/active",
      },
      {
        icon: <Star />,
        label: "Recommended",
        route: "/provider/jobs/recommended",
      },

      {
        icon: <Search />,
        label: "Find Jobs",
        route: "/provider/jobs/search",
      },
      {
        icon: <ThumbsUp />,
        label: "Interests",
        route: "/provider/jobs/interests",
      },
      {
        icon: <UserCheck2 />,
        label: "Organizations",
        route: "/provider/affiliations",
      },
    ],
  },
  {
    icon: <MessageSquare />,
    label: "Chat",
    route: "/provider/chat",
    sidebarItem: ProviderChatSidebarItem,
  },
  {
    divider: true,
  },
  {
    icon: <DollarSign />,
    label: "Refer Friends",
    route: "/provider/referrals",
  },
  {
    icon: <Wallet />,
    label: "Billing",
    route: "/provider/billing",
  },
  {
    icon: <Settings />,
    label: "Settings",
    route: "/provider/settings",
  },
];

const mobileNavItems = [
  {
    icon: <HouseIcon />,
    label: "Home",
    route: "/provider/home",
  },
  {
    icon: <FolderIcon />,
    label: "Documents",
    route: "/provider/documents",
  },
  {
    icon: <SquareUserRound />,
    label: "Profile",
    route: "/provider/profile",
  },
  {
    icon: <CalendarRangeIcon />,
    label: "Jobs",
    route: "/provider/jobs",
  },
  {
    route: "/provider/chat",
    label: "Chat",
    icon: <MessageSquare />,
    bottomMenutItem: ProviderChatBottomMenuItem,
  },
];

export function ProviderLayout({
  children,
  user,
  showSidebar = true,
  jobPostsEnabled = false,
}: {
  children: React.ReactNode;
  showSidebar?: boolean;
  user: User;
  jobPostsEnabled?: boolean;
}) {
  const [openSheet, setOpenSheet] = useState(false);
  const { collapsed, toggleCollapsed } = useSidebarCollapsed();
  const [expandedItems] = useState<string[]>([]);
  const router = useRouterState();
  const hideNav = router.location.search.hideNav;

  // Roster-only providers (marketplace job posts disabled) never browse or
  // find jobs and have no org/referral/billing surfaces. Collapse the
  // expandable "Jobs" section to a flat "My Work" link and hide the
  // Organizations, Refer Friends, and Billing items.
  const rosterHiddenLabels = ["Organizations", "Refer Friends", "Billing"];
  const myWorkItem = {
    icon: <CheckCircle />,
    label: "My Work",
    route: "/provider/jobs/active",
  };

  const filteredSideItems: typeof sideItems = jobPostsEnabled
    ? sideItems
    : sideItems
        .filter((item) => !rosterHiddenLabels.includes(item.label ?? ""))
        .map((item) => (item.label === "Jobs" ? myWorkItem : item));

  const filteredMobileNavItems: typeof mobileNavItems = jobPostsEnabled
    ? mobileNavItems
    : mobileNavItems.map((item) => (item.label === "Jobs" ? myWorkItem : item));

  // If hideNav is true, don't show any navigation
  if (hideNav) {
    return (
      <div className="min-h-dvh max-h-dvh w-full">
        <main className="flex-1 overflow-y-auto bg-gray-100">{children}</main>
      </div>
    );
  }

  const sidebarGridClass = collapsed
    ? "grid lg:grid-cols-[72px_1fr]"
    : "grid lg:grid-cols-[280px_1fr]";

  return (
    <>
      <div
        className={`max-h-svh min-h-svh w-full ${showSidebar ? sidebarGridClass : ""}`}
      >
        {showSidebar && (
          <div
            id="static-sidebar"
            className="hidden min-w-0 border-r border-slate-200 bg-white lg:block"
          >
            <TooltipProvider>
              <div className="flex h-full max-h-svh min-h-svh flex-col gap-2">
                <div
                  className={
                    collapsed
                      ? "flex h-14 shrink-0 items-center justify-center border-b border-slate-200 lg:h-[60px]"
                      : "flex h-14 shrink-0 items-center border-b border-slate-200 px-4 lg:h-[60px] lg:px-6"
                  }
                >
                  <div
                    className={
                      collapsed
                        ? "flex items-center gap-2 font-semibold"
                        : "mr-auto flex items-center gap-2 font-semibold"
                    }
                  >
                    {collapsed ? (
                      <img
                        alt="Saile Logo"
                        src="/static/images/logo_icon.png"
                        className="size-8"
                      />
                    ) : (
                      <img
                        alt="Saile Logo"
                        src="/static/images/logo.png"
                        className="w-[114px]"
                      />
                    )}
                  </div>
                  {!collapsed && (
                    <SidebarCollapseToggle
                      variant="header"
                      collapsed={collapsed}
                      onToggle={toggleCollapsed}
                    />
                  )}
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <nav
                    aria-label="Main navigation"
                    className="flex flex-col gap-1 px-3 py-4 text-sm"
                  >
                    {filteredSideItems.map((item) => {
                      if ("divider" in item) {
                        return (
                          <hr
                            key="divider"
                            className="my-2 border-t border-slate-200"
                          />
                        );
                      }

                      const isSelected = router.location.pathname.startsWith(
                        item.route || "",
                      );
                      const hasSelectedSubItem = item.subItems?.some(
                        (subItem) =>
                          router.location.pathname.startsWith(subItem.route),
                      );

                      const MenuItem = item.sidebarItem;
                      return (
                        <React.Fragment key={item.label}>
                          {MenuItem ? (
                            <MenuItem item={item} collapsed={collapsed} />
                          ) : (
                            <SidebarItem
                              item={
                                item.divider
                                  ? item
                                  : {
                                      icon: item.icon,
                                      label: item.label,
                                      route: item.route,
                                    }
                              }
                              hasSelectedSubItem={hasSelectedSubItem}
                              variant="primary"
                              collapsed={collapsed}
                            />
                          )}
                          {item.subItems &&
                            (isSelected || hasSelectedSubItem) && (
                              <div
                                className={
                                  collapsed
                                    ? "flex min-w-0 flex-col gap-1 rounded-xl bg-slate-50"
                                    : "ml-3 flex min-w-0 flex-col gap-1 border-l border-slate-200 pl-2"
                                }
                              >
                                {item.subItems.map((subItem) => (
                                  <SidebarItem
                                    key={subItem.label}
                                    item={subItem}
                                    variant="sub"
                                    collapsed={collapsed}
                                    className={
                                      collapsed
                                        ? "hover:bg-slate-100"
                                        : undefined
                                    }
                                  />
                                ))}
                              </div>
                            )}
                        </React.Fragment>
                      );
                    })}
                  </nav>
                </div>
                <SidebarCollapseToggle
                  variant="footer"
                  collapsed={collapsed}
                  onToggle={toggleCollapsed}
                />
              </div>
            </TooltipProvider>
          </div>
        )}
        <div className="flex flex-col min-h-dvh max-h-dvh">
          <header
            id="header"
            className="flex items-center gap-4 border-b bg-muted py-2 box-border px-4 py-2"
          >
            {showSidebar && (
              <Sheet open={openSheet} onOpenChange={setOpenSheet}>
                <SheetTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="shrink-0 lg:hidden"
                  >
                    <Menu className="h-5 w-5" />
                    <span className="sr-only">Toggle navigation menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent
                  id="sidebar"
                  side="left"
                  className="flex h-auto flex-col rounded-r-2xl border-slate-200 bg-white px-4 pb-[max(1rem,var(--ion-safe-area-bottom,0px))]"
                >
                  <SheetTitle className="sr-only">
                    Provider Navigation
                  </SheetTitle>
                  <img
                    alt="Saile Logo"
                    src="/static/images/logo.png"
                    className="ml-2 w-[114px] shrink-0"
                  />
                  <nav
                    aria-label="Main navigation"
                    className="-mx-1 flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-1 py-1"
                  >
                    {filteredSideItems.map((item) => {
                      if (item.divider) {
                        return (
                          <hr
                            key="divider"
                            className="my-2 border-t border-slate-200"
                          />
                        );
                      }

                      const hasSelectedSubItem = item.subItems?.some(
                        (subItem) =>
                          router.location.pathname.startsWith(subItem.route),
                      );
                      const isExpanded =
                        expandedItems.includes(item?.label || "") ||
                        hasSelectedSubItem;

                      const MenuItem = item.sidebarItem;
                      return (
                        <React.Fragment key={item.label}>
                          {MenuItem ? (
                            <MenuItem
                              onClick={() => {
                                setOpenSheet(false);
                              }}
                              item={
                                item as {
                                  icon: React.ReactNode;
                                  label: string;
                                  route?: string;
                                }
                              }
                            />
                          ) : (
                            <SidebarItem
                              item={
                                item as {
                                  icon: React.ReactNode;
                                  label: string;
                                  route?: string;
                                }
                              }
                              onClick={() => {
                                setOpenSheet(false);
                              }}
                              hasSelectedSubItem={hasSelectedSubItem}
                              variant="primary"
                            />
                          )}
                          {item.subItems && isExpanded && (
                            <div className="ml-3 flex min-w-0 flex-col gap-1 border-l border-slate-200 pl-2">
                              {item.subItems.map((subItem) => (
                                <SidebarItem
                                  key={subItem.label}
                                  item={subItem}
                                  onClick={() => {
                                    setOpenSheet(false);
                                  }}
                                  variant="sub"
                                />
                              ))}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </nav>
                </SheetContent>
              </Sheet>
            )}
            <div className="w-full flex-1 flex justify-center items-center">
              <img
                alt="Saile Logo"
                src="/static/images/logo.png"
                className="w-[114px] md:hidden"
              />
            </div>
            <UserAvatar minimal={!showSidebar} user={user} />
          </header>

          <main className="flex-1 flex flex-col relative overflow-y-auto bg-gray-100 max-w-dvw">
            {children}
          </main>

          {/* Mobile Bottom Navigation */}
          {showSidebar && (
            <BottomBar className="lg:hidden">
              {filteredMobileNavItems.map((item) => {
                const MenuItem = item.bottomMenutItem;
                return MenuItem ? (
                  <MenuItem key={item.label} item={item} />
                ) : (
                  <BottomBarItem key={item.label} item={item} />
                );
              })}
            </BottomBar>
          )}
        </div>
      </div>
      <DownloadApp jobPostsEnabled={jobPostsEnabled} />
    </>
  );
}
